/**
 * Utility functions for working with themes and domains
 */

/**
 * Generate a short name for a theme title with ellipsis if truncated
 * @param {string} title - The full theme title
 * @param {number} maxLength - Maximum length for the short name (default: 12)
 * @returns {string} Shortened title with ellipsis if needed
 */
export const generateShortName = (title, maxLength = 12) => {
  if (!title) return ''
  
  // If title is already short enough, return as is
  if (title.length <= maxLength) {
    return title
  }
  
  // Try to find a good break point at word boundaries
  const words = title.split(' ')
  let shortName = words[0]
  
  // Add more words if they fit
  for (let i = 1; i < words.length; i++) {
    const nextWord = words[i]
    const potential = shortName + ' ' + nextWord
    
    // If adding the next word would exceed the limit, stop
    if (potential.length + 3 > maxLength) { // +3 for "..."
      break
    }
    
    shortName = potential
  }
  
  // Add ellipsis if we truncated
  return shortName.length < title.length ? shortName + '...' : shortName
}

/**
 * Convert themes to domains format for RadarChart and other components
 * @param {Array} themes - Array of theme objects from questionnaire
 * @param {number} maxShortNameLength - Maximum length for short names (default: 12)
 * @returns {Array} Array of domain objects with name, shortName, and icon
 */
export const themesToDomains = (themes, maxShortNameLength = 12) => {
  if (!themes || !Array.isArray(themes)) {
    return []
  }

  return themes.map(theme => ({
    name: theme.title,
    shortName: generateShortName(theme.title, maxShortNameLength),
    icon: theme.icon
  }))
}

/**
 * Calculate theme scores from individual question scores
 * @param {Array} themes - Array of theme objects
 * @param {Object} scores - Object with question IDs as keys and scores as values
 * @returns {Array} Array of theme average scores
 */
export const calculateThemeScores = (themes, scores) => {
  if (!themes || !scores) {
    return []
  }

  return themes.map(theme => {
    const themeQuestionScores = theme.questions
      .map(q => scores[q.id])
      .filter(score => score !== null && score !== undefined)
    
    return themeQuestionScores.length > 0 
      ? themeQuestionScores.reduce((sum, score) => sum + score, 0) / themeQuestionScores.length
      : 0
  })
}
